import _, { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { select as d3Select } from 'd3-selection';
import {
  differenceInYears,
  differenceInMilliseconds,
  subMilliseconds,
  isBefore,
  addMilliseconds,
  isAfter,
} from 'date-fns';
import { IS_CURRENT_BROWSER_FIREFOX } from '../../components/Vector/mfrchart/constants';
import {
  setDragStartXPosition,
  setLatestVectorChartPan,
  getLatestDragXPosition,
  setLatestDragXPosition,
  setLatestDragEventDate,
  getLatestDragEventDate,
  getLatestVectorChartPan,
  getDragStartXPosition,
} from '../../components/Vector/util';
import { ZOOM_WIDTH_CONSTRAINTS } from '../constants';
import useDomEvent from './useDomEvent';
import { DOM_EVENTS } from '../../components/PerformanceIndicators/utils';

let firefoxCurrentXPosition = 0;
let allowDrag = false;
let firstVisibleDate = '';
let lastVisibleDate = '';
const transitionSpeed = 5;

/* eslint-disable func-names */
const useUpdatesTimelineEvents = (
  {
    selectedPeriod,
    visibleAreaWidthInPixels,
    onResetTimeline,
    rangeStartDate,
    rangeEndDate,
  },
  dep,
) => {
  const dayLinesContainerRef = useRef<SVGGElement>(null);
  const updatesContainerRef = useRef<SVGGElement>(null);
  const labelsContainerRef = useRef<SVGGElement>(null);
  const itemsContainerRef = useRef<SVGGElement>(null);
  const eventsContainerRef = useRef<HTMLDivElement>(null);
  const prevXRef = useRef(0);
  const panRef = useRef(0);

  const resetTimeline = useCallback(() => {
    if (itemsContainerRef.current) {
      const itemsContainerD3 = d3Select(itemsContainerRef.current);
      itemsContainerD3.attr('transform', `translate(0, 0)`);
      setDragStartXPosition(0);
      setLatestVectorChartPan(0); // not state
      panRef.current = 0;
    }
    const containers = [
      dayLinesContainerRef,
      updatesContainerRef,
      labelsContainerRef,
    ];
    containers.forEach((container) => {
      if (container.current) {
        container.current.innerHTML = '';
      }
    });
    onResetTimeline();
  }, []);

  useEffect(() => {
    return () => {
      resetTimeline();
    };
  }, dep);

  const zoomTrigger = (ev) => {
    const domain = ev.detail;
    if (differenceInYears(domain[0], domain[1]) < 1) {
      allowDrag = true;
    } else {
      allowDrag = false;
    }
    d3Select(itemsContainerRef.current).attr('transform', `translate(0, 0)`);
    panRef.current = 0;
  };

  useDomEvent(DOM_EVENTS.ZOOM_FROM_KPI_CHART, zoomTrigger, []);

  // Calculate the total time range in milliseconds
  const totalRange = useMemo(
    () =>
      differenceInMilliseconds(
        new Date(rangeEndDate),
        new Date(rangeStartDate),
      ),
    [rangeEndDate, rangeStartDate],
  );

  const getValidatedPanValue = (newPanValue: number) => {
    let updatedPan = 0;
    if (itemsContainerRef.current) {
      const bbox = itemsContainerRef.current.getBBox();
      const MIN_PAN_VALUE = -(bbox.width - visibleAreaWidthInPixels + bbox.x);
      const MAX_PAN_VALUE = -bbox.x;
      updatedPan = newPanValue > MAX_PAN_VALUE ? MAX_PAN_VALUE : newPanValue;
      updatedPan = updatedPan < MIN_PAN_VALUE ? MIN_PAN_VALUE : updatedPan;
    }
    return updatedPan;
  };

  const handleDragStart = (event) => {
    if (!allowDrag) {
      return;
    }

    const screenXAtTheTime = _.cloneDeep(event.screenX);
    event.dataTransfer.setDragImage(new Image(), 0, 0);
    setDragStartXPosition(screenXAtTheTime);
    setLatestVectorChartPan(panRef.current); // not state
  };

  // Firefox-specific; used because of old Firefox bug
  // that prevents receiving mouse coordinates in the drag event
  function handleDragOver(event) {
    if (!allowDrag) {
      return;
    }

    const screenXAtTheTime = _.cloneDeep(event.screenX);
    if (IS_CURRENT_BROWSER_FIREFOX) {
      firefoxCurrentXPosition = screenXAtTheTime;
      sendDomainToKpiChart([firstVisibleDate, lastVisibleDate]);
    }
  }

  const handleDrag = (event) => {
    if (!allowDrag) {
      return;
    }

    const screenXAtTheTime = IS_CURRENT_BROWSER_FIREFOX
      ? firefoxCurrentXPosition
      : _.cloneDeep(event.screenX);

    if (
      screenXAtTheTime !== getLatestDragXPosition() &&
      screenXAtTheTime !== 0
    ) {
      setLatestDragXPosition(screenXAtTheTime);
      const currentDragEventDate = new Date();
      setLatestDragEventDate(currentDragEventDate);
      if (screenXAtTheTime) {
        setTimeout(() => {
          if (
            currentDragEventDate.getTime() >= getLatestDragEventDate().getTime()
          ) {
            const latestPanValue = getLatestVectorChartPan();
            const latestDragStartX = getDragStartXPosition();
            const dragEndX = screenXAtTheTime;
            if (dragEndX !== 0) {
              const dragDistance = dragEndX - latestDragStartX;
              let newPanValue = latestPanValue + dragDistance;
              newPanValue = getValidatedPanValue(newPanValue);
              d3Select(itemsContainerRef.current).attr(
                'transform',
                `translate(${newPanValue}, 0)`,
              );
              panRef.current = newPanValue;
              firstVisibleDate = '';
              lastVisibleDate = '';
              // Get the width of the element
              const elementWidth = dayLinesContainerRef.current
                ? dayLinesContainerRef.current.getBoundingClientRect().width
                : 0;

              // Calculate how much time one pixel represents
              const timePerPixelInMs = totalRange / elementWidth;
              d3Select(dayLinesContainerRef.current)
                .selectAll('g')
                .each(function () {
                  const ele = d3Select(this as any).select('rect:first-child');
                  const xAttr = ele.attr('x');
                  const dateAttribute = ele.attr('date');
                  const actualX = Number(xAttr) + newPanValue;
                  if (actualX > 0 && firstVisibleDate === '') {
                    const newDate = subMilliseconds(
                      new Date(dateAttribute),
                      timePerPixelInMs * actualX,
                    );
                    if (isBefore(newDate, rangeStartDate)) {
                      firstVisibleDate = rangeStartDate.toString();
                    } else {
                      firstVisibleDate = newDate.toString();
                    }
                  }
                  if (actualX > 0 && actualX <= visibleAreaWidthInPixels) {
                    const newDate = addMilliseconds(
                      new Date(dateAttribute),
                      timePerPixelInMs * (visibleAreaWidthInPixels - actualX),
                    );
                    if (isAfter(newDate, rangeEndDate)) {
                      lastVisibleDate = rangeEndDate.toString();
                    } else {
                      lastVisibleDate = newDate.toString();
                    }
                  }
                });
              sendDomainToKpiChart([firstVisibleDate, lastVisibleDate]);
            }
          }
        }, 0);
      }
    }
  };

  const sendDomainToKpiChart = debounce((domain: string[]) => {
    document.dispatchEvent(
      new CustomEvent(DOM_EVENTS.ZOOM_FROM_TIMELINE, {
        detail: domain,
      }),
    );
  }, 50);

  const handleDragEnd = (event) => {
    const dragEndX = event.screenX;
    if (dragEndX !== 0) {
      firefoxCurrentXPosition = 0;
    }
  };

  const handleWheel = (e) => {
    const { deltaY, clientX } = e;
    const isZoomOut = deltaY > 0;
    const itemsContainerElement = itemsContainerRef.current;
    const dayLinesContainerElement = dayLinesContainerRef.current;
    const updatesContainerElement = updatesContainerRef.current;
    const labelsContainerElement = labelsContainerRef.current;
    if (
      itemsContainerElement &&
      dayLinesContainerElement &&
      updatesContainerElement
    ) {
      if (
        itemsContainerElement.getBoundingClientRect().width >
          ZOOM_WIDTH_CONSTRAINTS[selectedPeriod] &&
        !isZoomOut
      ) {
        return;
      }
      allowDrag = true;

      const dayLinesContainer = d3Select(dayLinesContainerElement);
      const rects = dayLinesContainer.selectAll('g').selectAll('rect');
      let translateXValue = 0;

      const computedStyle = window.getComputedStyle(itemsContainerElement);
      const transformValue = computedStyle.getPropertyValue('transform');
      const matrixRegex =
        /matrix\((-?\d+\.?\d*),\s*-?\d+\.?\d*,\s*-?\d+\.?\d*,\s*-?\d+\.?\d*,\s*(-?\d+\.?\d*),\s*-?\d+\.?\d*\)/; // Regex pattern to match the matrix values
      const match = transformValue.toString().match(matrixRegex);
      if (match && match[2]) {
        translateXValue = parseFloat(match[2]);
      }

      const rect = eventsContainerRef!.current!.getBoundingClientRect();
      const offsetLeft = rect.left;
      const offsetX = clientX - offsetLeft - translateXValue;
      let shouldResetTimeline = false;
      prevXRef.current = 0;
      firstVisibleDate = '';
      lastVisibleDate = '';
      const elementWidth =
        dayLinesContainerElement.getBoundingClientRect().width;
      const timePerPixelInMs = totalRange / elementWidth;
      dayLinesContainer.selectAll('g').each(function (d, i) {
        const firstChild = d3Select(this as any).select('rect:first-child');

        const currentX = Number(firstChild.attr('x'));
        const newX = getNewXValue(offsetX, currentX, isZoomOut);

        // if the first element is dragged to the right and newX is positive,
        // or the last element is dragged to the left and the newX is less then the width of the timeline
        // then reset the timeline
        if (
          (newX + panRef.current > 0 && i === 0) ||
          (i === rects.size() - 1 &&
            newX + panRef.current < visibleAreaWidthInPixels - 10)
        ) {
          shouldResetTimeline = true;
          return false;
        }

        firstChild.attr('x', newX);
        // check if this is the first positive newX and if so store it in firstVisibleDate
        const dateAttribute = d3Select(this as any)
          .select('rect:first-child')
          .attr('date');

        const actualX = newX + panRef.current;
        if (actualX > 0 && firstVisibleDate === '') {
          const newDate = subMilliseconds(
            new Date(dateAttribute),
            timePerPixelInMs * actualX,
          );
          // check if newDate is before rangeStartDate with date-fns
          if (isBefore(newDate, rangeStartDate)) {
            firstVisibleDate = rangeStartDate.toString();
          } else {
            firstVisibleDate = newDate.toString();
          }
        }

        if (actualX > 0 && actualX <= visibleAreaWidthInPixels) {
          const newDate = addMilliseconds(
            new Date(dateAttribute),
            timePerPixelInMs * (visibleAreaWidthInPixels - actualX),
          );
          if (isAfter(newDate, rangeEndDate)) {
            lastVisibleDate = rangeEndDate.toString();
          } else {
            lastVisibleDate = newDate.toString();
          }
        }

        d3Select(this as any).attr('x', newX);
        return true;
      });

      if (shouldResetTimeline) {
        d3Select(itemsContainerElement).attr('transform', `translate(0, 0)`);
        setDragStartXPosition(0);
        setLatestVectorChartPan(0);
        panRef.current = 0;
        firstVisibleDate = '';
        lastVisibleDate = '';
        allowDrag = true;
        onResetTimeline();
        shouldResetTimeline = false;
        document.dispatchEvent(new CustomEvent(DOM_EVENTS.ZOOM_FROM_TIMELINE));

        return;
      }

      sendDomainToKpiChart([firstVisibleDate, lastVisibleDate]);
      d3Select(labelsContainerElement)
        .selectAll('g')
        .attr('x', function () {
          const firstChild = d3Select(this as any).select('text:first-child');
          const currentX = Number(firstChild.attr('x'));
          const newX = getNewXValue(offsetX, currentX, isZoomOut);
          firstChild.attr('x', newX);
          return newX;
        });

      updateGElements(updatesContainerElement, isZoomOut, offsetX);
    }
  };

  function updateGElements(
    container: SVGGElement,
    isZoomOut: boolean,
    offsetX: number,
  ) {
    const updates = d3Select(container).selectAll('g');
    updates.attr('x', function () {
      const svgChild = d3Select(this).select('svg');
      const circleChild = d3Select(this).select('circle');
      const rectChild = d3Select(this).select('rect');
      const textChild = d3Select(this).select('text');
      const currentX = Number(rectChild.attr('x'));
      const newX = getNewXValue(offsetX, currentX, isZoomOut);
      circleChild.attr('cx', newX);
      textChild.attr('x', newX);
      rectChild.attr('x', newX);
      svgChild.attr('x', newX - 10);

      return newX;
    });
  }

  const getNewXValue = (
    offsetX: number,
    currentX: number,
    isZoomOut: boolean,
  ) => {
    const step = isZoomOut
      ? -Math.abs(offsetX - currentX) / transitionSpeed
      : Math.abs(offsetX - currentX) / transitionSpeed;
    return offsetX < currentX ? currentX + step : currentX - step;
  };

  return {
    dayLinesContainerRef,
    updatesContainerRef,
    labelsContainerRef,
    itemsContainerRef,
    eventsContainerRef,
    panRef,
    handleDragStart,
    handleDragEnd,
    handleDrag,
    handleWheel,
    handleDragOver,
  };
};

export default useUpdatesTimelineEvents;
